import React, { useEffect, useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Card, Typography, Tag } from 'antd'

import {
  selectExtraImportLogById,
  isFetchingImportLogs,
  getExtraImportLogById,
  getExtraImportLogFile,
  getExtraImportLogsTypes,
  selectAllExtraImportLogsTypes,
} from '../../../state/modules/importLogs'

import PageLayout from '../../layouts/DetailPageLayout'
import Table from '../../components/Tables/SearchTable'
import useDispatchHttp from '../../../hooks/dispatchHttpHandler'
import { mapImportLogsStatusColor, capitalizeEveryWord } from '../../../utils/helpers'

const RenderImportLogTableTitle = ({ file_name }) => {
  const { id } = useParams()
  const dispatch = useDispatch()

  return (
    <Typography.Link
      onClick={() => {
        dispatch(getExtraImportLogFile(id))
      }}
    >
      {file_name}
    </Typography.Link>
  )
}

const RenderImportLogTableStatus = status => (
  <Tag color={mapImportLogsStatusColor(status)}>{status}</Tag>
)

const detailColumns = [
  {
    title: 'ID',
    key: 'id',
  },
  {
    title: 'Date | Time',
    key: 'started_at',
    date: true,
  },
  {
    title: 'Title',
    key: 'data_file',
    render: RenderImportLogTableTitle,
  },
  {
    title: 'Status',
    key: 'status',
    render: RenderImportLogTableStatus,
  },
]

const statisticColumns = [
  {
    title: 'Total Rows',
    key: 'total_rows',
  },
  {
    title: 'Skipped',
    key: 'skipped',
  },
  {
    title: 'New Facility Mappings',
    key: 'new_facility_mappings',
  },
  {
    title: 'Removed Facility Mappings',
    key: 'removed_facility_mappings',
  },
  {
    title: 'New Doctor Mappings',
    key: 'new_doctor_mappings',
  },
  {
    title: 'Removed Doctor Mappings',
    key: 'removed_doctor_mappings',
  },
  {
    title: 'Duration',
    key: 'duration',
  },
]

const VendorImportDetails = () => {
  const dispatchHttp = useDispatchHttp()
  const { id } = useParams()

  const { search, state } = useLocation()
  const query = useMemo(() => new URLSearchParams(search), [search])
  const importType = query.get('import_type')

  const importLog = useSelector(currentState =>
    selectExtraImportLogById(currentState, id)
  )
  const loading = useSelector(isFetchingImportLogs)
  const importTypes = useSelector(selectAllExtraImportLogsTypes)

  const title = importLog?.data_file?.file_name || `Import #${id}`

  const currentImportTypeObj = importTypes.find(
    type => String(type.id) === String(importType)
  )

  const importTypeName = currentImportTypeObj
    ? capitalizeEveryWord(currentImportTypeObj.name)
    : '...'

  const backLinkParams = useMemo(() => {
    const params = new URLSearchParams()
    params.set('tab', '4')

    if (state) {
      params.set('import_type', state.returnImportType || importType)
      params.set('page', state.returnPage || 1)
      params.set('pageSize', state.returnPageSize || 10)
    } else {
      params.set('import_type', importType)
      params.set('page', '1')
      params.set('pageSize', '10')
    }
    return params.toString()
  }, [state, importType])

  const breadcrumbs = [
    { name: 'Import Log', path: '/data-sync-log' },
    {
      name: importTypeName,
      path: `/data-sync-log?${backLinkParams}`,
    },
    {
      name: title,
      path: `/vendor-import-log/${id}`,
    },
  ]

  useEffect(() => {
    if (id && importType) {
      dispatchHttp(
        getExtraImportLogById({
          id,
          import_type: importType,
        })
      )
    }
  }, [dispatchHttp, id, importType])

  useEffect(() => {
    if (importTypes.length === 0) {
      dispatchHttp(getExtraImportLogsTypes())
    }
  }, [dispatchHttp, importTypes.length])

  return (
    <PageLayout breadcrumbs={breadcrumbs} title={title}>
      <Card style={{ width: '100%' }}>
        <Table
          columns={detailColumns}
          tableTitle="Import Details"
          loading={loading}
          totalData={importLog ? [importLog] : []}
        />
        <Table
          columns={statisticColumns}
          tableTitle="Import Statistics"
          loading={loading}
          totalData={importLog ? [importLog] : []}
        />
      </Card>
      {importLog?.process_logs && (
        <Card loading={loading}>
          <Typography.Paragraph>
            <pre>{importLog?.process_logs}</pre>
          </Typography.Paragraph>
        </Card>
      )}
    </PageLayout>
  )
}

export default VendorImportDetails
